import { useState, useEffect } from "react";
import { mockDrawProgress } from "../../../mocks/account";
import { Link } from "react-router-dom";

function useCountdown(targetDate: Date) {
  const calc = () => {
    const diff = targetDate.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff / 3600000) % 24),
      minutes: Math.floor((diff / 60000) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };
  const [time, setTime] = useState(calc());
  useEffect(() => {
    const iv = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(iv);
  });
  return time;
}

const phases = [
  { id: "open", label: "Verkauf offen", done: true },
  { id: "closing", label: "Verkaufsschluss", done: false },
  { id: "draw", label: "Offizielle Ziehung", done: false },
  { id: "results", label: "Bekanntgabe der Ergebnisse", done: false },
];

export default function DrawTracker() {
  const target = new Date("2025-04-15T20:00:00");
  const { days, hours, minutes, seconds } = useCountdown(target);
  const prog = mockDrawProgress;
  const progressPct = Math.round((prog.ticketsSold / prog.ticketsTotal) * 100);
  const pad = (n: number) => String(n).padStart(2, "0");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-bold text-slate-900 text-xl font-playfair">Ziehungsverfolgung #52</h2>
        <p className="text-slate-500 text-sm mt-0.5">Offizielle Ziehung am 15. April 2025 um 20:00 Uhr</p>
      </div>

      {/* Countdown */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-7 text-white">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse inline-block"></span>
          <span className="text-slate-300 text-sm font-medium uppercase tracking-wider">Countdown</span>
        </div>
        <div className="flex items-center gap-4 mb-6">
          {[
            { val: pad(days), label: "Tage" },
            { val: pad(hours), label: "Stunden" },
            { val: pad(minutes), label: "Min" },
            { val: pad(seconds), label: "Sek" },
          ].map((t, i) => (
            <div key={i} className="text-center">
              <div className="bg-white/10 rounded-xl w-16 h-16 flex items-center justify-center mb-1">
                <span className="font-playfair text-3xl font-bold">{t.val}</span>
              </div>
              <span className="text-slate-400 text-xs uppercase">{t.label}</span>
            </div>
          ))}
        </div>

        {/* My participation */}
        <div className="bg-white/10 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-slate-300 text-xs uppercase tracking-wider mb-1">Ihre Lose in dieser Ziehung</p>
            <p className="text-white font-bold text-2xl">{prog.myTicketsCount} Lose</p>
          </div>
          <div className="text-right">
            <p className="text-slate-300 text-xs uppercase tracking-wider mb-1">Gewinnwahrscheinlichkeit</p>
            <p className="text-secondary-light font-bold text-2xl">~{prog.myWinProbability.toFixed(2)}%</p>
          </div>
        </div>
      </div>

      {/* Progress Bar: Tickets sold */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800 text-sm">Verkaufsfortschritt</h3>
          <span className="text-slate-500 text-sm font-medium">{prog.ticketsSold.toLocaleString()} / {prog.ticketsTotal.toLocaleString()}</span>
        </div>
        <div className="h-4 bg-slate-100 rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-gradient-to-r from-secondary to-secondary-light rounded-full transition-all duration-1000"
            style={{ width: `${progressPct}%` }}
          ></div>
        </div>
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>{progressPct}% der Lose vergriffen</span>
          <span>{(prog.ticketsTotal - prog.ticketsSold).toLocaleString()} verbleibend</span>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
          <div className="text-sm text-slate-500">
            Je näher die Ziehung rückt, desto mehr steigen Ihre Chancen!
          </div>
          <Link
            to="/forfaits"
            className="text-primary hover:text-primary-dark text-sm font-semibold flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            + Kaufen
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line text-sm"></i></span>
          </Link>
        </div>
      </div>

      {/* Phase tracker */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <h3 className="font-semibold text-slate-800 mb-5">Phasen der Ziehung</h3>
        <div className="relative">
          {/* Line */}
          <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-slate-100"></div>
          <div className="space-y-5">
            {phases.map((phase, i) => (
              <div key={phase.id} className="flex items-start gap-4 relative">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 ${
                  phase.done ? "bg-secondary" : i === 1 ? "bg-orange-100 border-2 border-primary-light" : "bg-slate-100"
                }`}>
                  {phase.done ? (
                    <i className="ri-check-line text-white text-sm"></i>
                  ) : i === 1 ? (
                    <i className="ri-time-line text-primary-light text-sm"></i>
                  ) : (
                    <span className="text-slate-400 text-xs font-bold">{i + 1}</span>
                  )}
                </div>
                <div className="pt-1">
                  <p className={`text-sm font-medium ${phase.done ? "text-secondary-dark" : i === 1 ? "text-primary-dark" : "text-slate-400"}`}>
                    {phase.label}
                  </p>
                  {i === 0 && <p className="text-xs text-slate-400 mt-0.5">Seit dem 15. Jan. 2025</p>}
                  {i === 1 && <p className="text-xs text-primary-light mt-0.5 font-medium">15. April 2025 um 18:00 Uhr</p>}
                  {i === 2 && <p className="text-xs text-slate-400 mt-0.5">15. April 2025 um 20:00 Uhr — live</p>}
                  {i === 3 && <p className="text-xs text-slate-400 mt-0.5">Innerhalb von 24 Std. nach der Ziehung</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Grand Prix reminder */}
      <div className="relative rounded-2xl overflow-hidden">
        <div className="w-full h-44">
          <img
            src="https://readdy.ai/api/search-image?query=breathtaking%20luxury%20villa%20at%20twilight%20with%20glowing%20interior%20lights%2C%20modern%20architecture%2C%20infinity%20pool%20reflecting%20sky%2C%20palm%20trees%20silhouette%2C%20premium%20real%20estate%20dream%20home%20exterior%2C%20elegant%20sophisticated%20estate&width=800&height=352&seq=draw-tracker-villa&orientation=landscape"
            alt="Villa du grand prix"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20"></div>
        <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
          <div>
            <p className="text-white/70 text-xs uppercase tracking-wider">Grand Prix</p>
            <p className="text-white font-bold text-lg">Villa Moderne 500 m²</p>
          </div>
          <p className="text-primary-light font-bold text-2xl font-playfair">1 200 000 €</p>
        </div>
      </div>
    </div>
  );
}
